import { useState, useMemo, useEffect, useRef } from 'react';
import { ProyectoMock, EvaluacionItem } from '../../../mocks/proyectos';
import EvaluadoresMemoria from './EvaluadoresMemoria';
import { useDocumentosDB } from '../../../hooks/useDocumentosDB';

interface Props {
  proyectos: ProyectoMock[];
  onUpdateProyecto: (codigo: string, updates: Partial<ProyectoMock>) => void;
}

const PREFIJOS = ['Dr.', 'Dra.', 'Mg.', 'PhD.', 'Lic.', 'Ing.', 'Prof.'];

const CRITERIOS = [
  { key: 'relevancia', label: 'Relevancia científica', icon: 'ri-star-line', desc: 'Importancia y pertinencia del problema abordado' },
  { key: 'metodologia', label: 'Rigor metodológico', icon: 'ri-tools-line', desc: 'Solidez del diseño y método de investigación' },
  { key: 'viabilidad', label: 'Viabilidad del proyecto', icon: 'ri-calendar-check-line', desc: 'Factibilidad técnica, temporal y presupuestal' },
  { key: 'innovacion', label: 'Grado de innovación', icon: 'ri-lightbulb-line', desc: 'Novedad y originalidad del enfoque propuesto' },
  { key: 'impacto', label: 'Impacto social / ambiental', icon: 'ri-earth-line', desc: 'Beneficio esperado para la comunidad o el entorno' },
  { key: 'originalidad', label: 'Aporte al conocimiento', icon: 'ri-bookmark-line', desc: 'Contribución original al campo de estudio' },
  { key: 'equipo', label: 'Competencia del equipo', icon: 'ri-team-line', desc: 'Idoneidad y experiencia del grupo investigador' },
  { key: 'presupuesto', label: 'Justificación presupuestal', icon: 'ri-money-dollar-circle-line', desc: 'Adecuación y coherencia del presupuesto con los objetivos' },
];

const DEFAULT_SCORES: Record<string, string> = {};
CRITERIOS.forEach(c => { DEFAULT_SCORES[c.key] = ''; });

function calcularPromedio(scores: Record<string, string>): number | null {
  const vals = Object.values(scores).map(v => parseInt(v, 10)).filter(v => !isNaN(v));
  if (vals.length === 0) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function getScoreColor(avg: number): string {
  if (avg >= 4) return 'text-emerald-600';
  if (avg >= 3) return 'text-teal-600';
  if (avg >= 2) return 'text-amber-600';
  return 'text-red-500';
}

function getScoreBg(avg: number): string {
  if (avg >= 4) return 'bg-emerald-50 border-emerald-200';
  if (avg >= 3) return 'bg-teal-50 border-teal-200';
  if (avg >= 2) return 'bg-amber-50 border-amber-200';
  return 'bg-red-50 border-red-200';
}

function getScoreLabel(avg: number): string {
  if (avg >= 4.5) return 'Excelente';
  if (avg >= 4) return 'Muy bueno';
  if (avg >= 3) return 'Bueno';
  if (avg >= 2) return 'Regular';
  return 'Deficiente';
}

const getEstadoBadge = (estado: string) => {
  if (estado === 'En evaluación') return 'bg-amber-50 text-amber-700 border border-amber-200';
  if (estado === 'Aprobado') return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  if (estado === 'Con correcciones') return 'bg-orange-50 text-orange-700 border border-orange-200';
  if (estado === 'No pertinente') return 'bg-red-50 text-red-600 border border-red-200';
  return 'bg-slate-50 text-slate-600 border border-slate-200';
};

export default function EvaluacionProyecto({ proyectos, onUpdateProyecto }: Props) {
  const [selectedCodigo, setSelectedCodigo] = useState<string | null>(null);
  const [evaluadorPrefijo, setEvaluadorPrefijo] = useState('');
  const [evaluadorNombre, setEvaluadorNombre] = useState('');
  const [scores, setScores] = useState<Record<string, string>>({ ...DEFAULT_SCORES });
  const [observaciones, setObservaciones] = useState('');
  const [ethicsFlag, setEthicsFlag] = useState('');
  const [decisionPropuesta, setDecisionPropuesta] = useState<'aprobado' | 'correcciones' | 'no_pertinente' | ''>('');
  const [saved, setSaved] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showPrevEvals, setShowPrevEvals] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [hasDoc, setHasDoc] = useState(false);
  const [docsMap, setDocsMap] = useState<Record<string, boolean>>({});
  const [downloadingCard, setDownloadingCard] = useState<string | null>(null);
  const [yaEvaluadoDismissed, setYaEvaluadoDismissed] = useState(false);

  const evalPanelRef = useRef<HTMLDivElement>(null);
  const { hasDocumento, downloadDocumento } = useDocumentosDB();

  const evaluadorCompleto = useMemo(
    () => [evaluadorPrefijo, evaluadorNombre.trim()].filter(Boolean).join(' '),
    [evaluadorPrefijo, evaluadorNombre],
  );

  const canEvaluate = evaluadorNombre.trim() !== '';

  const proyecto = useMemo(
    () => proyectos.find(p => p.codigo === selectedCodigo),
    [proyectos, selectedCodigo],
  );

  // Reactively detect if this evaluator already evaluated the selected project
  const yaEvaluado = useMemo(() => {
    if (!evaluadorCompleto || !proyecto) return false;
    return !!proyecto.evaluaciones?.find(
      e => e.evaluador.toLowerCase() === evaluadorCompleto.toLowerCase()
    );
  }, [evaluadorCompleto, proyecto]);
  const avgScore = useMemo(() => calcularPromedio(scores), [scores]);
  const completedCriterios = Object.values(scores).filter(v => v !== '').length;

  // Reset dismissed banner when project or evaluator changes
  useEffect(() => {
    setYaEvaluadoDismissed(false);
  }, [selectedCodigo, evaluadorCompleto]);

  // Check if project has a document in IndexedDB
  useEffect(() => {
    if (!selectedCodigo) { setHasDoc(false); return; }
    hasDocumento(selectedCodigo).then(setHasDoc).catch(() => setHasDoc(false));
  }, [selectedCodigo, hasDocumento]);

  // Build a map of which projects have documents (for the list cards)
  useEffect(() => {
    if (proyectos.length === 0) return;
    Promise.all(
      proyectos.map(p => hasDocumento(p.codigo).then(has => ({ codigo: p.codigo, has })).catch(() => ({ codigo: p.codigo, has: false })))
    ).then(results => {
      const map: Record<string, boolean> = {};
      results.forEach(r => { map[r.codigo] = r.has; });
      setDocsMap(map);
    });
  }, [proyectos, hasDocumento]);

  // Auto-load evaluation when evaluador + project are both selected
  useEffect(() => {
    if (!proyecto || !evaluadorCompleto) return;
    const myEval = proyecto.evaluaciones?.find(
      e => e.evaluador.toLowerCase() === evaluadorCompleto.toLowerCase(),
    );
    if (myEval) {
      setScores({ ...DEFAULT_SCORES, ...myEval.scores });
      setObservaciones(myEval.observaciones || '');
      setEthicsFlag(myEval.ethicsFlag || '');
      setDecisionPropuesta((myEval.decisionPropuesta as 'aprobado' | 'correcciones' | 'no_pertinente' | '') || '');
    } else {
      setDecisionPropuesta('');
    }
  }, [selectedCodigo, evaluadorCompleto, proyecto]);

  const handleSelectProject = (codigo: string) => {
    setSelectedCodigo(codigo);
    setSaved(false);
    setShowDetail(false);
    setShowPrevEvals(false);
    setScores({ ...DEFAULT_SCORES });
    setObservaciones('');
    setEthicsFlag('');
    setDecisionPropuesta('');
    setTimeout(() => {
      evalPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  const handleDownloadFromCard = async (e: React.MouseEvent, codigo: string) => {
    e.stopPropagation();
    setDownloadingCard(codigo);
    try { await downloadDocumento(codigo); } finally { setDownloadingCard(null); }
  };

  // Called from the EvaluadoresMemoria sidebar
  const handleMemoriaSelect = (codigo: string, evaluadorFullName: string) => {
    const found = PREFIJOS.find(p => evaluadorFullName.startsWith(p + ' '));
    if (found) {
      setEvaluadorPrefijo(found);
      setEvaluadorNombre(evaluadorFullName.slice(found.length + 1).trim());
    } else {
      setEvaluadorPrefijo('');
      setEvaluadorNombre(evaluadorFullName.trim());
    }
    setSelectedCodigo(codigo);
    setSaved(false);
    setShowDetail(false);
    setShowPrevEvals(false);
    setScores({ ...DEFAULT_SCORES });
    setObservaciones('');
    setEthicsFlag('');
    setDecisionPropuesta('');
    setTimeout(() => {
      evalPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  const handleDownloadDoc = async () => {
    if (!selectedCodigo) return;
    setDownloading(true);
    try { await downloadDocumento(selectedCodigo); } finally { setDownloading(false); }
  };

  const decisionLabel: Record<string, string> = {
    aprobado: 'Aprobado científicamente',
    correcciones: 'Aprobado con correcciones',
    no_pertinente: 'No pertinente',
  };

  const handleSave = () => {
    if (!selectedCodigo || !canEvaluate || !proyecto) return;
    const newEval: EvaluacionItem = {
      evaluador: evaluadorCompleto,
      fecha: new Date().toISOString().split('T')[0],
      scores: { ...scores },
      observaciones,
      ethicsFlag,
      recomendacion: decisionPropuesta ? decisionLabel[decisionPropuesta] : '',
      decisionPropuesta,
    };
    const existingEvals = proyecto.evaluaciones || [];
    const idx = existingEvals.findIndex(
      e => e.evaluador.toLowerCase() === evaluadorCompleto.toLowerCase(),
    );
    const newEvals: EvaluacionItem[] = idx >= 0
      ? existingEvals.map((e, i) => (i === idx ? newEval : e))
      : [...existingEvals, newEval];

    onUpdateProyecto(selectedCodigo, { evaluaciones: newEvals, estado: 'En evaluación' });
    setSaved(true);
    setTimeout(() => setSaved(false), 4000);
  };

  const prevEvals = proyecto?.evaluaciones || [];

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-8 flex items-center justify-center bg-teal-600 rounded-md">
            <i className="ri-microscope-line text-white text-lg" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800">Evaluación del Proyecto</h2>
        </div>
        <p className="text-slate-500 text-sm ml-11">
          Panel para miembros del Comité de Investigación e Interacción. Ingrese su nombre para registrar su evaluación de forma independiente.
        </p>

        {/* Guía rápida para el evaluador */}
        <div className="mt-4 bg-teal-50 border border-teal-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-5 h-5 flex items-center justify-center text-teal-600 flex-shrink-0">
              <i className="ri-information-line text-base" />
            </span>
            <p className="text-sm font-semibold text-teal-800">Guía para el evaluador</p>
          </div>

          {/* Pasos */}
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            {[
              { num: '1', icon: 'ri-user-star-line', text: 'Ingrese su nombre completo en el campo de identificación.' },
              { num: '2', icon: 'ri-file-list-3-line', text: 'Seleccione el proyecto a evaluar de la lista.' },
              { num: '3', icon: 'ri-star-line', text: 'Califique cada criterio del 1 al 5 y emita su recomendación.' },
              { num: '4', icon: 'ri-save-line', text: 'Guarde su evaluación. Puede editarla hasta que el Administrador emita la decisión final.' },
            ].map(step => (
              <div key={step.num} className="flex items-start gap-2 flex-1 bg-white border border-teal-100 rounded-md px-3 py-2.5">
                <span className="w-5 h-5 flex items-center justify-center bg-teal-600 text-white text-xs font-bold rounded-full flex-shrink-0 mt-0.5">
                  {step.num}
                </span>
                <p className="text-xs text-slate-600 leading-relaxed">{step.text}</p>
              </div>
            ))}
          </div>

          {/* Escala de decisión */}
          <div>
            <p className="text-xs font-semibold text-teal-700 mb-2 flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-bar-chart-line" /></span>
              Escala de calificación y criterio de decisión
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                <span className="w-4 h-4 flex items-center justify-center text-emerald-600 flex-shrink-0 mt-0.5">
                  <i className="ri-checkbox-circle-fill text-sm" />
                </span>
                <div>
                  <p className="text-xs font-bold text-emerald-700">Aprobado sin correcciones</p>
                  <p className="text-xs text-emerald-600">Promedio <strong>4.0 – 5.0</strong> · El proyecto cumple todos los criterios de calidad científica.</p>
                </div>
              </div>
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                <span className="w-4 h-4 flex items-center justify-center text-amber-600 flex-shrink-0 mt-0.5">
                  <i className="ri-edit-circle-fill text-sm" />
                </span>
                <div>
                  <p className="text-xs font-bold text-amber-700">Aprobado con correcciones</p>
                  <p className="text-xs text-amber-600">Promedio <strong>2.5 – 3.9</strong> · El proyecto es viable pero requiere ajustes antes de su ejecución.</p>
                </div>
              </div>
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                <span className="w-4 h-4 flex items-center justify-center text-red-500 flex-shrink-0 mt-0.5">
                  <i className="ri-close-circle-fill text-sm" />
                </span>
                <div>
                  <p className="text-xs font-bold text-red-600">No pertinente</p>
                  <p className="text-xs text-red-500">Promedio <strong>1.0 – 2.4</strong> · El proyecto no cumple los requisitos mínimos de calidad o pertinencia.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2-column layout */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">

        {/* ── LEFT: main evaluation panel ── */}
        <div className="xl:col-span-2 space-y-5">

          {/* Identificación del evaluador */}
          <div className="bg-white border border-teal-200 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-teal-800 mb-4 flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-user-star-line text-teal-600" /></span>
              Identificación del miembro evaluador <span className="text-red-500 ml-0.5">*</span>
            </h3>
            <div className="flex gap-2">
              {/* Prefijo */}
              <div className="flex-shrink-0">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Prefijo</label>
                <div className="relative">
                  <select
                    value={evaluadorPrefijo}
                    onChange={e => setEvaluadorPrefijo(e.target.value)}
                    className="border border-slate-200 rounded-md pl-3 pr-7 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white cursor-pointer appearance-none text-slate-700"
                  >
                    <option value="">—</option>
                    {PREFIJOS.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 flex items-center justify-center text-slate-400 pointer-events-none">
                    <i className="ri-arrow-down-s-line text-xs" />
                  </span>
                </div>
              </div>
              {/* Nombre */}
              <div className="flex-1">
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Nombre completo <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={evaluadorNombre}
                  onChange={e => setEvaluadorNombre(e.target.value)}
                  placeholder="Ej.: Roberto Ortega"
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                />
              </div>
            </div>

            {/* Preview del nombre completo */}
            {evaluadorCompleto && (
              <div className="mt-3 flex items-center gap-2 bg-teal-50 border border-teal-100 rounded-md px-3 py-2">
                <span className="w-4 h-4 flex items-center justify-center text-teal-600 flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-sm" />
                </span>
                <p className="text-sm text-teal-800">
                  Evaluando como: <strong>{evaluadorCompleto}</strong>
                </p>
              </div>
            )}


          </div>

          {/* Lista de todos los proyectos */}
          <div>
            <h3 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center text-teal-600"><i className="ri-list-check-2" /></span>
              Todos los proyectos ({proyectos.length})
            </h3>
            {proyectos.length === 0 ? (
              <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-8 text-center text-slate-400 text-sm">
                No hay proyectos registrados aún. Aparecerán aquí cuando se presenten.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-2">
                {proyectos.map((p) => {
                  const numEvals = p.evaluaciones?.length || 0;
                  const promedios = (p.evaluaciones || []).map(e => calcularPromedio(e.scores)).filter((v): v is number => v !== null);
                  const avgGlobal = promedios.length > 0 ? promedios.reduce((a, b) => a + b, 0) / promedios.length : null;
                  const myEval = evaluadorCompleto
                    ? p.evaluaciones?.find(e => e.evaluador.toLowerCase() === evaluadorCompleto.toLowerCase())
                    : undefined;

                  const cardHasDoc = docsMap[p.codigo] || !!p.documentoNombre;

                  return (
                    <div
                      key={p.codigo}
                      onClick={() => handleSelectProject(p.codigo)}
                      className={`cursor-pointer w-full text-left rounded-lg border transition-all overflow-hidden ${
                        selectedCodigo === p.codigo
                          ? 'border-teal-500 bg-teal-50'
                          : myEval
                          ? 'border-emerald-400 bg-emerald-50/50 hover:border-emerald-500'
                          : 'border-slate-200 bg-white hover:border-teal-300 hover:bg-teal-50/40'
                      }`}
                    >
                      {/* Franja superior verde si ya fue evaluado */}
                      {myEval && (
                        <div className="bg-emerald-500 px-4 py-1 flex items-center gap-2">
                          <span className="w-3 h-3 flex items-center justify-center text-white">
                            <i className="ri-checkbox-circle-fill text-xs" />
                          </span>
                          <span className="text-xs font-semibold text-white">
                            Ya evaluado por {evaluadorCompleto}
                          </span>
                          {myEval.recomendacion && (
                            <span className="ml-auto text-xs text-emerald-100 font-medium">
                              Recomendación: {myEval.recomendacion}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                              <p className="text-xs text-teal-600 font-semibold">{p.codigo}</p>
                              {numEvals > 0 && (
                                <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-medium">
                                  {numEvals} evaluador{numEvals !== 1 ? 'es' : ''}
                                </span>
                              )}
                              {avgGlobal !== null && (
                                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${getScoreBg(avgGlobal)}`}>
                                  Prom. {avgGlobal.toFixed(1)}/5
                                </span>
                              )}
                            </div>
                            <p className="text-sm font-semibold text-slate-800 truncate">{p.titulo}</p>
                            <p className="text-xs text-slate-500">{p.investigador} · {p.area} · Recibido: {p.fechaRecepcion}</p>
                          </div>
                          <div className="flex flex-col items-end gap-2 flex-shrink-0">
                            <span className={`whitespace-nowrap text-xs px-2.5 py-1 rounded-full font-medium ${getEstadoBadge(p.estado)}`}>
                              {p.estado}
                            </span>
                            {cardHasDoc ? (
                              <button
                                onClick={e => handleDownloadFromCard(e, p.codigo)}
                                disabled={downloadingCard === p.codigo}
                                title="Descargar documento del proyecto"
                                className="whitespace-nowrap flex items-center gap-1 text-xs font-semibold text-emerald-700 border border-emerald-300 bg-emerald-50 rounded-md px-2 py-1 hover:bg-emerald-100 transition-colors cursor-pointer disabled:opacity-60"
                              >
                                <span className="w-3 h-3 flex items-center justify-center">
                                  <i className={downloadingCard === p.codigo ? 'ri-loader-4-line animate-spin' : 'ri-download-2-line'} />
                                </span>
                                {downloadingCard === p.codigo ? 'Descargando...' : 'Descargar doc.'}
                              </button>
                            ) : (
                              <span className="whitespace-nowrap flex items-center gap-1 text-xs text-slate-400 border border-slate-200 bg-slate-50 rounded-md px-2 py-1">
                                <span className="w-3 h-3 flex items-center justify-center"><i className="ri-file-unknow-line" /></span>
                                Sin documento
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Panel de evaluación */}
          {proyecto ? (
            <div ref={evalPanelRef} className="bg-white border border-slate-200 rounded-lg p-6">
              {/* Encabezado del proyecto */}
              <div className="flex items-start justify-between gap-4 mb-5">
                <div className="flex-1">
                  <p className="text-xs font-semibold text-teal-600 mb-0.5">{proyecto.codigo} · {proyecto.area}</p>
                  <h3 className="text-base font-semibold text-slate-800">{proyecto.titulo}</h3>
                  <p className="text-sm text-slate-500">{proyecto.investigador}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                  {prevEvals.length > 0 && (
                    <button
                      onClick={() => setShowPrevEvals(v => !v)}
                      className="whitespace-nowrap flex items-center gap-1.5 text-xs text-slate-600 border border-slate-200 px-2.5 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer transition-all"
                    >
                      <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-team-line" /></span>
                      {prevEvals.length} eval. previas
                    </button>
                  )}
                  {proyecto.resumen && (
                    <button
                      onClick={() => setShowDetail(v => !v)}
                      className="whitespace-nowrap flex items-center gap-1.5 text-xs text-teal-600 border border-teal-200 px-2.5 py-1.5 rounded-md hover:bg-teal-50 cursor-pointer transition-all"
                    >
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className={showDetail ? 'ri-eye-off-line' : 'ri-eye-line'} />
                      </span>
                      {showDetail ? 'Ocultar' : 'Ver detalle'}
                    </button>
                  )}
                  {/* Botón descarga documento */}
                  {(hasDoc || proyecto.documentoNombre) ? (
                    <button
                      onClick={handleDownloadDoc}
                      disabled={downloading}
                      title="Descargar el documento del proyecto para revisarlo"
                      className="whitespace-nowrap flex items-center gap-1.5 text-xs font-semibold text-emerald-700 border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 rounded-md hover:bg-emerald-100 cursor-pointer transition-all disabled:opacity-60"
                    >
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className={downloading ? 'ri-loader-4-line animate-spin' : 'ri-download-2-line'} />
                      </span>
                      {downloading ? 'Descargando...' : 'Descargar documento'}
                    </button>
                  ) : (
                    <span
                      title="El investigador aún no ha subido el documento del proyecto"
                      className="whitespace-nowrap flex items-center gap-1.5 text-xs text-slate-400 border border-slate-200 bg-slate-50 px-2.5 py-1.5 rounded-md"
                    >
                      <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-file-unknow-line" /></span>
                      Sin documento
                    </span>
                  )}
                </div>
              </div>

              {/* Evaluaciones previas */}
              {showPrevEvals && prevEvals.length > 0 && (
                <div className="mb-5 bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-3 flex items-center gap-2">
                    <span className="w-4 h-4 flex items-center justify-center text-teal-600"><i className="ri-team-line" /></span>
                    Evaluaciones ya registradas ({prevEvals.length})
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs min-w-[500px]">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-2 pr-4 font-semibold text-slate-500 whitespace-nowrap">Evaluador</th>
                          <th className="text-left py-2 pr-4 font-semibold text-slate-500 whitespace-nowrap">Promedio</th>
                          <th className="text-left py-2 pr-4 font-semibold text-slate-500 whitespace-nowrap">Recomendación</th>
                          <th className="text-left py-2 font-semibold text-slate-500 whitespace-nowrap">Fecha</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {prevEvals.map((ev, i) => {
                          const avg = calcularPromedio(ev.scores);
                          return (
                            <tr key={i}>
                              <td className="py-2 pr-4 font-medium text-slate-700 whitespace-nowrap">{ev.evaluador}</td>
                              <td className="py-2 pr-4">
                                {avg !== null ? (
                                  <span className={`font-bold ${getScoreColor(avg)}`}>{avg.toFixed(1)}/5</span>
                                ) : <span className="text-slate-400">—</span>}
                              </td>
                              <td className="py-2 pr-4 text-slate-600">{ev.recomendacion || '—'}</td>
                              <td className="py-2 text-slate-400">{ev.fecha}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Detalle del proyecto */}
              {showDetail && (
                <div className="mb-5 bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-widest">Información detallada</h4>
                    {(hasDoc || proyecto.documentoNombre) && (
                      <button
                        onClick={handleDownloadDoc}
                        disabled={downloading}
                        className="whitespace-nowrap flex items-center gap-1.5 text-xs font-semibold text-emerald-700 border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 rounded-md hover:bg-emerald-100 cursor-pointer transition-all disabled:opacity-60"
                      >
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className={downloading ? 'ri-loader-4-line animate-spin' : 'ri-file-download-line'} />
                        </span>
                        {downloading ? 'Descargando...' : 'Descargar documento completo'}
                      </button>
                    )}
                  </div>
                  {(hasDoc || proyecto.documentoNombre) && (
                    <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                      <span className="w-4 h-4 flex items-center justify-center text-emerald-600 flex-shrink-0 mt-0.5">
                        <i className="ri-information-line text-sm" />
                      </span>
                      <p className="text-xs text-emerald-700">
                        El investigador adjuntó el documento completo del proyecto. Puede descargarlo para revisarlo antes de calificar.
                      </p>
                    </div>
                  )}
                  {proyecto.resumen && (
                    <div>
                      <p className="text-xs font-semibold text-slate-500 mb-1">Resumen ejecutivo</p>
                      <p className="text-xs text-slate-700 leading-relaxed">{proyecto.resumen}</p>
                    </div>
                  )}
                  {proyecto.objetivos && (
                    <div>
                      <p className="text-xs font-semibold text-slate-500 mb-1">Objetivos</p>
                      <p className="text-xs text-slate-700 leading-relaxed">{proyecto.objetivos}</p>
                    </div>
                  )}
                  {proyecto.diseno && (
                    <div>
                      <p className="text-xs font-semibold text-slate-500 mb-1">Diseño y metodología</p>
                      <p className="text-xs text-slate-700 leading-relaxed">{proyecto.diseno}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Banner: ya evaluó este proyecto — reactivo, siempre visible si aplica */}
              {yaEvaluado && canEvaluate && !yaEvaluadoDismissed && (
                <div className="mb-5 bg-emerald-50 border-2 border-emerald-400 rounded-lg p-4 flex items-start gap-3">
                  <span className="w-10 h-10 flex items-center justify-center bg-emerald-500 rounded-full flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-white text-2xl" />
                  </span>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-emerald-800 mb-1">
                      Este proyecto ya fue evaluado por usted
                    </p>
                    <p className="text-xs text-emerald-700 leading-relaxed">
                      <strong>{evaluadorCompleto}</strong>, ya registró su evaluación para este trabajo.
                      Sus calificaciones y recomendación están cargadas en el formulario.
                      Puede revisarlas y actualizar si lo considera necesario.
                    </p>
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
                      <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-information-line" /></span>
                      Para modificar su evaluación, edite los campos y presione &quot;Guardar mi evaluación&quot;.
                    </div>
                  </div>
                  <button
                    onClick={() => setYaEvaluadoDismissed(true)}
                    className="w-6 h-6 flex items-center justify-center text-emerald-400 hover:text-emerald-700 cursor-pointer flex-shrink-0 mt-0.5"
                    title="Cerrar aviso"
                  >
                    <i className="ri-close-line text-base" />
                  </button>
                </div>
              )}

              {/* Alerta si no está identificado */}
              {!canEvaluate && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-700 flex items-start gap-2 mb-5">
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-user-star-line" /></span>
                  Ingrese su <strong>nombre completo</strong> en la sección superior antes de guardar su evaluación.
                </div>
              )}

              {/* Score summary */}
              {avgScore !== null && (
                <div className={`flex items-center gap-4 border rounded-lg px-4 py-3 mb-5 ${getScoreBg(avgScore)}`}>
                  <div>
                    <p className="text-xs text-slate-500 mb-0.5">Mi puntuación promedio</p>
                    <p className={`text-2xl font-extrabold ${getScoreColor(avgScore)}`}>
                      {avgScore.toFixed(1)} <span className="text-sm font-normal text-slate-400">/ 5</span>
                    </p>
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-semibold ${getScoreColor(avgScore)}`}>{getScoreLabel(avgScore)}</p>
                    <p className="text-xs text-slate-500">{completedCriterios} de {CRITERIOS.length} criterios evaluados</p>
                  </div>
                  <div className="w-20 h-20 flex items-center justify-center flex-shrink-0">
                    <div className="relative w-16 h-16">
                      <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
                        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                        <circle
                          cx="18" cy="18" r="15.9" fill="none"
                          stroke={avgScore >= 4 ? '#059669' : avgScore >= 3 ? '#0d9488' : avgScore >= 2 ? '#d97706' : '#dc2626'}
                          strokeWidth="3"
                          strokeDasharray={`${(avgScore / 5) * 100} 100`}
                        />
                      </svg>
                      <p className={`absolute inset-0 flex items-center justify-center text-xs font-bold ${getScoreColor(avgScore)}`}>
                        {Math.round((avgScore / 5) * 100)}%
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Criterios de evaluación */}
              <div className="mb-5">
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-teal-600">
                    <i className="ri-list-check-2" />
                  </span>
                  Criterios de evaluación <span className="text-slate-400 font-normal text-xs">— Escala del 1 (deficiente) al 5 (excelente)</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {CRITERIOS.map(({ key, label, icon, desc }) => (
                    <div key={key} className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                      <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5 mb-1">
                        <span className="w-4 h-4 flex items-center justify-center text-teal-600"><i className={icon} /></span>
                        {label}
                      </label>
                      <p className="text-xs text-slate-400 mb-2">{desc}</p>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button
                            key={n}
                            onClick={() => setScores(prev => ({ ...prev, [key]: String(n) }))}
                            className={`whitespace-nowrap w-9 h-9 flex items-center justify-center rounded-md text-sm font-semibold border transition-all cursor-pointer ${
                              scores[key] === String(n)
                                ? 'bg-teal-600 text-white border-teal-600'
                                : 'border-slate-200 bg-white text-slate-500 hover:border-teal-400 hover:text-teal-600'
                            }`}
                          >
                            {n}
                          </button>
                        ))}
                        {scores[key] && (
                          <button
                            onClick={() => setScores(prev => ({ ...prev, [key]: '' }))}
                            className="w-7 h-7 flex items-center justify-center text-slate-300 hover:text-red-400 cursor-pointer"
                            title="Limpiar"
                          >
                            <i className="ri-close-line text-sm" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Comité de Ética */}
              <div className="mb-5 bg-white border border-slate-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-teal-600"><i className="ri-shield-check-line" /></span>
                  Comité de Ética
                </h4>
                <div className="flex flex-wrap gap-3">
                  {['Sí, obligatorio', 'Recomendado', 'No requerido'].map(opt => (
                    <label key={opt} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                      <input
                        type="radio"
                        name="ethics_eval"
                        value={opt}
                        checked={ethicsFlag === opt}
                        onChange={() => setEthicsFlag(opt)}
                        className="accent-teal-600"
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              </div>

              {/* Recomendación y decisión propuesta — sección unificada */}
              <div className="mb-5 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-amber-800 mb-1 flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-amber-600"><i className="ri-award-line" /></span>
                  Recomendación del evaluador
                  <span className="ml-auto text-xs font-normal bg-amber-100 text-amber-600 border border-amber-200 px-2 py-0.5 rounded-full">
                    Para consolidación del Administrador
                  </span>
                </h4>
                <p className="text-xs text-amber-600 mb-3 leading-relaxed">
                  Seleccione su recomendación. Se centralizará junto con las de los demás evaluadores; la decisión final la emite únicamente el Administrador Principal.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {([
                    { key: 'aprobado' as const, label: 'Aprobado científicamente', icon: 'ri-checkbox-circle-fill', active: 'border-emerald-400 bg-emerald-600 text-white', inactive: 'border-slate-200 bg-white text-slate-700 hover:border-emerald-300 hover:bg-emerald-50' },
                    { key: 'correcciones' as const, label: 'Aprobado con correcciones', icon: 'ri-edit-circle-fill', active: 'border-amber-400 bg-amber-500 text-white', inactive: 'border-slate-200 bg-white text-slate-700 hover:border-amber-300 hover:bg-amber-50' },
                    { key: 'no_pertinente' as const, label: 'No pertinente', icon: 'ri-close-circle-fill', active: 'border-red-400 bg-red-500 text-white', inactive: 'border-slate-200 bg-white text-slate-700 hover:border-red-300 hover:bg-red-50' },
                  ]).map(({ key, label, icon, active, inactive }) => (
                    <button
                      key={key}
                      onClick={() => setDecisionPropuesta(decisionPropuesta === key ? '' : key)}
                      className={`whitespace-nowrap flex flex-col items-center gap-1.5 px-3 py-3 rounded-md border text-xs font-semibold transition-all cursor-pointer ${
                        decisionPropuesta === key ? active : inactive
                      }`}
                    >
                      <span className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${icon} text-xl`} />
                      </span>
                      <span className="text-center leading-tight">{label}</span>
                    </button>
                  ))}
                </div>
                {decisionPropuesta && (
                  <p className="mt-2.5 text-xs text-amber-700 flex items-center gap-1.5">
                    <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-information-line" /></span>
                    Recomendación registrada: <strong>{decisionLabel[decisionPropuesta]}</strong>. El Administrador la verá en la consolidación.
                  </p>
                )}
              </div>

              {/* Observaciones */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Observaciones del evaluador <span className="text-slate-400 font-normal">({observaciones.length}/500)</span>
                </label>
                <textarea
                  value={observaciones}
                  onChange={e => e.target.value.length <= 500 && setObservaciones(e.target.value)}
                  rows={4}
                  placeholder="Fortalezas, debilidades, sugerencias de mejora para el investigador..."
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50 resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between gap-4">
                {saved && (
                  <span className="text-sm text-teal-600 flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-checkbox-circle-line" /></span>
                    Evaluación de <strong>{evaluadorCompleto}</strong> guardada
                  </span>
                )}
                <div className="ml-auto flex items-center gap-3">
                  <button
                    onClick={() => {
                      setScores({ ...DEFAULT_SCORES });
                      setObservaciones('');
                      setEthicsFlag('');
                      setDecisionPropuesta('');
                    }}
                    className="whitespace-nowrap px-4 py-2.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors cursor-pointer"
                  >
                    Limpiar
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={!canEvaluate}
                    className="whitespace-nowrap bg-teal-600 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-teal-700 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-save-line" /></span>
                    Guardar mi evaluación
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-10 text-center">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 text-slate-300">
                <i className="ri-file-search-line text-4xl" />
              </div>
              <p className="text-slate-500 text-sm">Seleccione un proyecto de la lista para comenzar la evaluación</p>
            </div>
          )}
        </div>

        {/* ── RIGHT: evaluadores memory panel ── */}
        <div className="xl:col-span-1">
          <div className="sticky top-6">
            <EvaluadoresMemoria
              proyectos={proyectos}
              evaluadorActivo={evaluadorCompleto}
              onSelectProject={handleMemoriaSelect}
            />
          </div>
        </div>

      </div>
    </div>
  );
}
